import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import WelcomeModal from './WelcomeModal'
import Projects from './Projects'
import Pitches from './Pitches'
import Income from './Income'
import RateCalculator from './RateCalculator'
import Profile from './Profile'
import Templates from './Templates'
import Settings from './Settings'

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: '⌂' },
  { id: 'projects', label: 'Projects', icon: '◫' },
  { id: 'pitches', label: 'Pitch Tracker', icon: '◎' },
  { id: 'income', label: 'Income', icon: '◈' },
  { id: 'calculator', label: 'Rate Calc', icon: '◇' },
  { id: 'templates', label: 'Templates', icon: '◱' },
  { id: 'portfolio', label: 'Portfolio', icon: '◰' },
  { id: 'profile', label: 'Profile', icon: '◉' },
  { id: 'settings', label: 'Settings', icon: '⚙' },
]

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function IncomeChart({ data, theme }) {
  const max = Math.max(...data.map(d => d.amount), 1)
  const chartHeight = 120

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: chartHeight + 28 }}>
      {data.map((d, i) => {
        const barHeight = Math.max((d.amount / max) * chartHeight, d.amount > 0 ? 4 : 0)
        const isCurrent = d.month === new Date().getMonth() && d.year === new Date().getFullYear()
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ position: 'relative', width: '100%', height: chartHeight, display: 'flex', alignItems: 'flex-end' }}>
              <div
                title={`${MONTH_LABELS[d.month]} ${d.year}: $${d.amount.toLocaleString()}`}
                style={{
                  width: '100%',
                  height: barHeight,
                  background: isCurrent ? theme.accent : `${theme.accent}55`,
                  borderRadius: '2px 2px 0 0',
                  transition: 'height 0.3s ease',
                  minHeight: 1,
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme.accent}
                onMouseLeave={e => e.currentTarget.style.background = isCurrent ? theme.accent : `${theme.accent}55`}
              />
            </div>
            <div style={{
              fontSize: 9,
              color: isCurrent ? theme.accent : theme.textMuted,
              fontFamily: "'Open Sans', sans-serif",
              letterSpacing: '0.05em',
              fontWeight: isCurrent ? 700 : 400,
            }}>{MONTH_LABELS[d.month]}</div>
          </div>
        )
      })}
    </div>
  )
}

export default function Dashboard({ session, theme, toggleTheme, applyThemeSettings, canEdit, isFounder, stripeCustomerId, subscriptionStatus, trialEndsAt, isBetaUser }) {
  const getActiveFromHash = () => {
    const hash = window.location.hash.replace('#', '')
    const valid = navItems.map(n => n.id)
    return valid.includes(hash) ? hash : 'dashboard'
  }

  const [active, setActive] = useState(getActiveFromHash)
  const [stats, setStats] = useState(null)
  const [displayName, setDisplayName] = useState('')
  const [targetIncome, setTargetIncome] = useState(null)
  const [avatarUrl, setAvatarUrl] = useState('')
  const [showWelcome, setShowWelcome] = useState(false)
  const [joinedAt, setJoinedAt] = useState(null)
  const [tourComplete, setTourComplete] = useState(false)

  const navigateTo = (id) => {
    window.location.hash = id === 'dashboard' ? '' : id
    setActive(id)
  }

  useEffect(() => {
    const handleHashChange = () => setActive(getActiveFromHash())
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // On login: fetch display name + saved theme preferences
  useEffect(() => {
    if (session?.user?.id) {
      fetchProfileData()
      fetchThemePrefs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchProfileData = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('display_name, target_income, avatar_url')
      .eq('user_id', session.user.id)
      .single()
    if (data?.display_name) setDisplayName(data.display_name)
    if (data?.target_income) setTargetIncome(data.target_income)
    if (data?.avatar_url) setAvatarUrl(data.avatar_url)
  }

  const fetchThemePrefs = async () => {
    const { data } = await supabase
      .from('settings')
      .select('theme_name, font_size, row_density')
      .eq('user_id', session.user.id)
      .single()
    if (data?.theme_name) {
      applyThemeSettings(data.theme_name, theme.mode, data.font_size, data.row_density)
    }
  }

  useEffect(() => {
    async function checkTour() {
      const { data } = await supabase
        .from('settings')
        .select('has_seen_tour')
        .eq('user_id', session.user.id)
        .single()
      if (!data || !data.has_seen_tour) {
        setShowWelcome(true)
      }
    }

    async function getJoinedAt() {
      const { data } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('user_id', session.user.id)
        .single()
      if (data) setJoinedAt(new Date(data.created_at))
    }

    checkTour()
    getJoinedAt()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  useEffect(() => {
    if (session?.user?.id && active === 'dashboard') {
      fetchDashboardData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, active])

  const fetchDashboardData = async () => {
    const uid = session.user.id
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth()
    const monthStart = new Date(currentYear, currentMonth, 1).toISOString().split('T')[0]
    const monthEnd = new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0]

    const [projectsRes, pitchesRes, incomeRes] = await Promise.all([
      supabase.from('projects').select('id, status, value').eq('user_id', uid),
      supabase.from('pitches').select('id, status').eq('user_id', uid),
      supabase.from('income').select('amount, status, invoice_date, date_paid').eq('user_id', uid),
    ])

    const projects = projectsRes.data || []
    const pitches = pitchesRes.data || []
    const income = incomeRes.data || []

    const activeProjects = projects.filter(p => p.status === 'In Progress').length
    const pipelineValue = projects
      .filter(p => p.status !== 'Complete')
      .reduce((sum, p) => sum + (Number(p.value) || 0), 0)

    const openPitches = pitches.filter(p => p.status !== 'Dead' && p.status !== 'Landed').length

    const thisMonthIncome = income
      .filter(i => {
        const dateStr = i.date_paid || i.invoice_date
        if (!dateStr) return false
        return dateStr >= monthStart && dateStr <= monthEnd
      })
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0)

    const thisYearIncome = income
      .filter(i => {
        const dateStr = i.date_paid || i.invoice_date
        if (!dateStr) return false
        return dateStr.startsWith(String(currentYear))
      })
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0)

    const awaitingPayment = income
      .filter(i => i.status === 'Invoiced')
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0)

    const chartData = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - i, 1)
      const y = d.getFullYear()
      const m = d.getMonth()
      const mStr = String(m + 1).padStart(2, '0')
      const prefix = `${y}-${mStr}`
      const amount = income
        .filter(inc => {
          const dateStr = inc.date_paid || inc.invoice_date
          return dateStr && dateStr.startsWith(prefix)
        })
        .reduce((sum, inc) => sum + (Number(inc.amount) || 0), 0)
      chartData.push({ month: m, year: y, amount })
    }

    setStats({ activeProjects, pipelineValue, openPitches, thisMonthIncome, thisYearIncome, awaitingPayment, chartData })
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
  }

  const handleBuy = async () => {
    const res = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: session.user.id, email: session.user.email }),
    })
    const { url } = await res.json()
    if (url) window.location.href = url
  }

  const trialDaysRemaining = trialEndsAt
    ? Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))
    : 0

  const showTrialBanner = subscriptionStatus === 'trialing' && !isBetaUser && trialEndsAt && new Date(trialEndsAt) > new Date()
  const showSubscribeBtn = !canEdit && !isBetaUser

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  return (
    <div style={{
      fontFamily: "'Nunito Sans', sans-serif",
      background: theme.bg,
      minHeight: '100vh',
      color: theme.text,
      display: 'flex',
    }}>
      {/* Sidebar */}
      <div style={{
        width: 230,
        background: theme.surface,
        borderRight: `1px solid ${theme.border}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{
          padding: '24px 20px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <img
            src={theme.logo}
            alt="Ravenwood"
            style={{ height: 70, width: 'auto' }}
            onError={e => { e.target.style.display = 'none' }}
          />
          <div>
            <div style={{ fontSize: 20, color: theme.text, fontFamily: "'Cardo', serif", letterSpacing: '0.05em' }}>
              Writer OS
            </div>
            <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif" }}>
              by Ravenwood
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
          {navItems.map(item => {
            const isActive = active === item.id
            return (
              <button
                key={item.id}
                onClick={() => navigateTo(item.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 20px', background: isActive ? theme.navActive : 'transparent',
                  border: 'none', borderLeft: isActive ? `3px solid ${theme.accent}` : '3px solid transparent',
                  color: isActive ? theme.navActiveText : theme.navText,
                  cursor: 'pointer', fontSize: 15, textAlign: 'left',
                  fontFamily: "'Nunito Sans', sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = theme.navActive }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
              >
                <span style={{ fontSize: 16, width: 18, textAlign: 'center' }}>{item.icon}</span>
                {item.label}
              </button>
            )
          })}
        </nav>

        {/* Feedback */}
        <div style={{ padding: '8px 12px' }}>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSfJO4SXgG43XDYRJMUQfjg0eLBV0rk1OIwqdZchDcbM0QRnLA/viewform?usp=sharing&ouid=103137438676853336593"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 8px', borderRadius: 4,
              color: theme.accent, fontSize: 13, textDecoration: 'none',
              fontFamily: "'Nunito Sans', sans-serif",
              border: `1px solid ${theme.accent}33`,
              background: `${theme.accent}0d`,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = `${theme.accent}1a`}
            onMouseLeave={e => e.currentTarget.style.background = `${theme.accent}0d`}
          >
            <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>◈</span>
            Give Feedback
          </a>
        </div>

        {/* Profile avatar */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${theme.border}` }}>
          <button
            onClick={() => navigateTo('profile')}
            title="Go to Profile"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, padding: 0, width: '100%',
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: avatarUrl ? 'transparent' : theme.accent,
              border: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{
                    fontSize: 13, fontFamily: "'Cardo', serif", fontWeight: 700,
                    color: theme.mode === 'dark' ? '#000' : '#fff',
                  }}>{displayName ? displayName[0].toUpperCase() : '?'}</span>
              }
            </div>
            <div style={{
              fontSize: 12, color: theme.textSecondary, fontFamily: "'Nunito Sans', sans-serif",
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {displayName || 'Profile'}
            </div>
          </button>
        </div>

        {/* Subscription buttons */}
        <div style={{ padding: '0 12px 4px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {showSubscribeBtn && (
            <button
              onClick={handleBuy}
              style={{
                width: '100%', background: theme.accent, border: 'none',
                color: '#0a2342', cursor: 'pointer', fontSize: 11, padding: '9px 8px',
                fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.08em',
                textTransform: 'uppercase', fontWeight: 700,
              }}
            >
              Buy Writer OS
            </button>
          )}
        </div>

        {/* Bottom controls */}
        <div style={{
          padding: '16px 20px', borderTop: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button onClick={handleSignOut} style={{
            background: 'transparent', border: 'none', color: theme.textMuted,
            cursor: 'pointer', fontSize: 12, fontFamily: "'Nunito Sans', sans-serif", padding: 0,
          }}>Sign out</button>

          {/* Icon-only light/dark toggle */}
          <button
            onClick={toggleTheme}
            title={`Switch to ${theme.mode === 'dark' ? 'light' : 'dark'} mode`}
            style={{
              background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
              color: theme.textSecondary, cursor: 'pointer', fontSize: 14,
              width: 30, height: 30, borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {theme.mode === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', zoom: theme.contentZoom }}>
        {/* Header */}
        <div style={{
          padding: '22px 36px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: theme.bg, zIndex: 10,
        }}>
          <div>
            <div style={{ fontSize: 22, color: theme.text, fontFamily: "'Cardo', serif" }}>
              {navItems.find(n => n.id === active)?.label}
            </div>
            <div style={{
              fontSize: 11, color: theme.textMuted, marginTop: 2,
              fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <div style={{ color: theme.accent, fontSize: 10, opacity: 0.5 }}>◆</div>
        </div>

        {/* View-only banner */}
        {!canEdit && (
          <div style={{
            borderLeft: `4px solid #d9ad2b`, background: '#fffdf7',
            padding: '10px 36px', fontSize: 12,
            color: '#0a2342', fontFamily: "'Nunito Sans', sans-serif",
          }}>
            You're in view-only mode.{' '}
            <button
              onClick={handleBuy}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#d9ad2b', fontWeight: 700, fontSize: 12, padding: 0,
                fontFamily: "'Nunito Sans', sans-serif", textDecoration: 'underline',
              }}
            >
              Buy Writer OS to unlock full access →
            </button>
          </div>
        )}

        {/* Trial banner */}
        {showTrialBanner && (
          <div style={{
            borderLeft: `4px solid ${theme.textSecondary}`, background: theme.surfaceAlt,
            padding: '10px 36px', fontSize: 12,
            color: theme.text, fontFamily: "'Nunito Sans', sans-serif",
          }}>
            Your free trial ends in {trialDaysRemaining < 1 ? 'less than 1 day' : `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'}`}.{' '}
            <button
              onClick={handleBuy}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: theme.textSecondary, fontWeight: 700, fontSize: 12, padding: 0,
                fontFamily: "'Nunito Sans', sans-serif", textDecoration: 'underline',
              }}
            >
              Buy Writer OS to keep full access →
            </button>
          </div>
        )}

        {!showWelcome && !tourComplete && joinedAt && ((new Date() - joinedAt) / (1000 * 60 * 60 * 24)) < 7 && (
          <div style={{
            background: theme.accent + '22',
            border: `1px solid ${theme.accent}44`,
            borderRadius: '8px',
            padding: '10px 16px',
            margin: '0 36px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '13px',
            color: theme.text,
          }}>
            <span>👋 New here? Check out the <strong>getting started guide</strong> to set everything up.</span>
            <button
              onClick={() => setShowWelcome(true)}
              style={{
                background: theme.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: '12px',
                whiteSpace: 'nowrap',
              }}
            >
              View guide →
            </button>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '32px 36px' }}>
          {active === 'dashboard' && (
            <div>
              {/* Welcome card */}
              <div style={{
                background: theme.surface, border: `1px solid ${theme.border}`,
                borderLeft: `4px solid ${theme.accent}`, padding: '24px 32px', marginBottom: 24,
                display: 'flex', alignItems: 'center', gap: 20,
              }}>
                {avatarUrl && (
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                    border: `2px solid ${theme.accent}40`,
                  }}>
                    <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div>
                  <div style={{
                    fontSize: 11, color: theme.accent, fontFamily: "'Open Sans', sans-serif",
                    letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6,
                  }}>Welcome back</div>
                  <div style={{ fontSize: 24, color: theme.text, fontFamily: "'Cardo', serif" }}>
                    {greeting()}{displayName ? `, ${displayName}` : ''}.
                  </div>
                  {targetIncome && (
                    <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginTop: 4 }}>
                      Target: ${Number(targetIncome).toLocaleString()} / year
                    </div>
                  )}
                </div>
              </div>

              {/* Stat cards row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  {
                    label: 'Active Projects',
                    value: stats ? stats.activeProjects : '—',
                    sub: stats ? `$${stats.pipelineValue.toLocaleString()} pipeline` : null,
                    onClick: () => navigateTo('projects'),
                  },
                  {
                    label: 'Open Pitches',
                    value: stats ? stats.openPitches : '—',
                    sub: null,
                    onClick: () => navigateTo('pitches'),
                  },
                  {
                    label: 'This Month',
                    value: stats ? `$${stats.thisMonthIncome.toLocaleString()}` : '—',
                    sub: stats ? `$${stats.awaitingPayment.toLocaleString()} awaiting` : null,
                    onClick: () => navigateTo('income'),
                  },
                ].map((stat, i) => (
                  <div key={i} onClick={stat.onClick} style={{
                    background: theme.surface, border: `1px solid ${theme.border}`,
                    padding: '20px 24px', cursor: 'pointer', transition: 'border-color 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
                  >
                    <div style={{
                      fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
                      textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
                    }}>{stat.label}</div>
                    <div style={{ fontSize: 32, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 6 }}>
                      {stat.value}
                    </div>
                    {stat.sub && (
                      <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>
                        {stat.sub}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Income chart + year stat */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
                <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '24px 28px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div>
                      <div style={{
                        fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
                        textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 4,
                      }}>Income — Last 12 Months</div>
                      <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>
                        Hover a bar to see the amount
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: theme.accent, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.1em' }}>
                      ◆ current month
                    </div>
                  </div>
                  {stats
                    ? <IncomeChart data={stats.chartData} theme={theme} />
                    : <div style={{ height: 148, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted, fontSize: 13 }}>Loading...</div>
                  }
                </div>

                <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '24px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{
                    fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
                    textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
                  }}>Earned {new Date().getFullYear()}</div>
                  <div style={{ fontSize: 38, color: theme.accent, fontFamily: "'Cardo', serif", lineHeight: 1, marginBottom: 12 }}>
                    {stats ? `$${stats.thisYearIncome.toLocaleString()}` : '—'}
                  </div>
                  <div style={{ width: '100%', height: 1, background: theme.border, marginBottom: 16 }} />
                  <div style={{
                    fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
                    textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8,
                  }}>Awaiting Payment</div>
                  <div style={{ fontSize: 28, color: theme.text, fontFamily: "'Cardo', serif", lineHeight: 1 }}>
                    {stats ? `$${stats.awaitingPayment.toLocaleString()}` : '—'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {active === 'projects' && <Projects session={session} theme={theme} canEdit={canEdit} />}
          {active === 'pitches' && <Pitches session={session} theme={theme} canEdit={canEdit} />}
          {active === 'income' && <Income session={session} theme={theme} canEdit={canEdit} />}
          {active === 'calculator' && <RateCalculator session={session} theme={theme} canEdit={canEdit} />}
          {active === 'templates' && <Templates session={session} theme={theme} canEdit={canEdit} />}
          {active === 'profile' && <Profile session={session} theme={theme} canEdit={canEdit} isFounder={isFounder} onNameChange={setDisplayName} onAvatarChange={url => setAvatarUrl(url)} />}
          {active === 'settings' && <Settings session={session} theme={theme} applyThemeSettings={applyThemeSettings} toggleTheme={toggleTheme} subscriptionStatus={subscriptionStatus} trialEndsAt={trialEndsAt} isBetaUser={isBetaUser} isFounder={isFounder} stripeCustomerId={stripeCustomerId} onShowWelcome={() => setShowWelcome(true)} />}
          {active === 'portfolio' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: 30, color: theme.text, letterSpacing: '0.18em',
                textTransform: 'uppercase', fontFamily: "'Cardo', sans-serif",
                marginBottom: 24,
              }}>Coming Soon...</div>
              <div style={{
                fontSize: 17, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif",
                marginBottom: 24,
              }}>Track your writing samples and publish a public portfolio page. Coming in a future update.</div>
              <img
                src="/images/portfolio-mockup.jpg"
                alt="Portfolio mockup"
                style={{ width: '60%', display: 'block', margin: '0 auto' }}
              />
            </div>
          )}
          {active !== 'dashboard' && active !== 'projects' && active !== 'pitches' && active !== 'income' && active !== 'calculator' && active !== 'templates' && active !== 'profile' && active !== 'settings' && active !== 'portfolio' && (
            <div style={{
              background: theme.surface, border: `1px solid ${theme.border}`,
              padding: '48px', textAlign: 'center',
            }}>
              <div style={{
                fontSize: 11, color: theme.textMuted, fontFamily: "'Open Sans', sans-serif",
                letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>
                {navItems.find(n => n.id === active)?.label} — coming soon
              </div>
            </div>
          )}
        </div>
      </div>
      {showWelcome && (
        <WelcomeModal
          session={session}
          theme={theme}
          onClose={() => setShowWelcome(false)}
          onNavigate={(panel) => setActive(panel)}
          onAllDone={() => setTourComplete(true)}
        />
      )}
    </div>
  )
}